import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Filter, Download, FileText, ShieldCheck, X, QrCode, Image as ImageIcon, Tag, Mail, ChevronsUpDown, ArrowUp, ArrowDown, ClipboardCheck, FolderOpen, Barcode, ChevronDown, PackageCheck } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import RecipientsField from "@/components/RecipientsField";
import { addEmails } from "@/lib/emailHistory";
import { getActiveToken } from "@/lib/sessionToken";
import { ProductStatus } from "@/components/ProductStatus";

const EMPTY_FILTERS = {
  q: "", brand_id: "", category_id: "", subcategory_id: "",
  code: "", barcode: "", inci: "", inci_min_percent: "", cas: "",
  price_min: "", price_max: "",
};

export default function Downloads() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [priceBounds, setPriceBounds] = useState({ min: 0, max: 500 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [onlyReady, setOnlyReady] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [langModal, setLangModal] = useState(false);
  const [qrModal, setQrModal] = useState(false);
  const [barcodeModal, setBarcodeModal] = useState(false);
  const [assetMenuOpen, setAssetMenuOpen] = useState(false);
  const [expandedKind, setExpandedKind] = useState(null);
  const [emailExpandedKind, setEmailExpandedKind] = useState(null);
  const [emailModal, setEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({
    kinds: { sds: true, cpnp: false, cpnp_notification: false, pif: false, packaging_certification: false, labels: false, photos: false, qr: false, barcode: false },
    recipients: "",
    subject: "",
    message: "",
    langs: { en: true, fr: false, es: false, de: false, pt: false },
    qr_format: "png",
    barcode_format: "png",
  });
  const [cpnpLangs, setCpnpLangs] = useState({ en: true, fr: false, es: false, de: false, pt: false });
  const [qrFormat, setQrFormat] = useState("png");
  const [barcodeFormat, setBarcodeFormat] = useState("png");
  const [busy, setBusy] = useState(null); // "sds" | "cpnp" | "photos" | "labels" | "qr" | "email" | null

  const fetchAll = async (f = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([_, v]) => v !== "" && v !== null));
      params.include_export_status = true;
      params.light = true;
      params.limit = 10000;
      const r = await api.get("/products", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { toast.error("Errore caricamento prodotti"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/brands").then(r => setBrands(r.data));
    api.get("/categories").then(r => setCats(r.data));
    api.get("/products/price-range").then(r => {
      const min = Math.floor(r.data.min || 0);
      const max = Math.ceil(r.data.max || 500);
      setPriceBounds({ min, max: max > min ? max : min + 1 });
    }).catch(() => {});
    fetchAll();
  }, []);

  // Live text search: re-fetch (debounced) as the user types, no "Cerca" click.
  const skipFirstSearch = useRef(true);
  useEffect(() => {
    if (skipFirstSearch.current) { skipFirstSearch.current = false; return; }
    const t = setTimeout(() => { fetchAll(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  const brandMap = useMemo(() => Object.fromEntries(brands.map(b => [b.id, b])), [brands]);
  const catMap = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c])), [cats]);
  const subcats = cats.filter(c => c.parent_id);
  const topCats = cats.filter(c => !c.parent_id);

  // Sorting — persists field/direction in localStorage (keys shared with Dashboard)
  const [sortField, setSortField] = useState(() => {
    const saved = localStorage.getItem("cosm_downloads_sort_field") || "";
    return saved === "packaging_certification" ? "" : saved;
  });
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("cosm_downloads_sort_dir") || "asc");
  useEffect(() => {
    localStorage.setItem("cosm_downloads_sort_field", sortField);
    localStorage.setItem("cosm_downloads_sort_dir", sortDir);
  }, [sortField, sortDir]);
  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  // Filter visible items when "only ready" is on (products with at least one exportable asset:
  // SDS, INCI approved, labels, or packaging certification). Then apply sort.
  const visibleItems = useMemo(() => {
    const base = onlyReady
      ? items.filter(p => p.has_sds || p.inci_approved || p.has_labels || p.has_packaging_certification)
      : items;
    if (!sortField) return base;
    const collator = new Intl.Collator("it", { sensitivity: "base", numeric: true });
    const getKey = (p) => {
      switch (sortField) {
        case "code": return (p.code || "").trim();
        case "name": return (p.name || "").trim();
        case "brand": return (brandMap[p.brand_id]?.name || "").trim();
        case "category": return (catMap[p.category_id]?.name || "").trim();
        case "sds": return p.has_sds ? 1 : 0;
        case "inci": return p.inci_approved ? 1 : 0;
        case "cpnp_notification": return p.has_cpnp_notification ? 1 : 0;
        case "pif": return p.has_pif ? 1 : 0;
        case "photos": return p.has_photos ? 1 : 0;
        case "labels": return p.has_labels ? 1 : 0;
        case "barcode": return p.has_barcode ? 1 : 0;
        default: return "";
      }
    };
    const arr = base.slice();
    arr.sort((a, b) => {
      const va = getKey(a), vb = getKey(b);
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      return collator.compare(String(va), String(vb));
    });
    if (sortDir === "desc") arr.reverse();
    return arr;
  }, [items, onlyReady, sortField, sortDir, brandMap, catMap]);

  const allVisibleSelected = visibleItems.length > 0 && visibleItems.every(p => selected.has(p.id));

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleItems.forEach(p => next.delete(p.id));
      } else {
        visibleItems.forEach(p => next.add(p.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const selectedItems = useMemo(() => items.filter(p => selected.has(p.id)), [items, selected]);
  const selectedWithSds = selectedItems.filter(p => p.has_sds).length;
  const selectedWithInci = selectedItems.filter(p => p.inci_approved).length;
  const selectedWithPhotos = selectedItems.filter(p => p.has_photos).length;
  const selectedWithLabels = selectedItems.filter(p => p.has_labels).length;
  const selectedWithCpnpNotif = selectedItems.filter(p => p.has_cpnp_notification).length;
  const selectedWithPif = selectedItems.filter(p => p.has_pif).length;
  const selectedWithPackagingCertification = selectedItems.filter(p => p.has_packaging_certification).length;
  const selectedBrandNames = [];
  const brandNamesSeen = new Set();
  selectedItems.forEach(p => {
    const name = brandMap[p.brand_id]?.name?.trim() || "Prodotti senza brand";
    if (!brandNamesSeen.has(name.toLocaleLowerCase())) {
      selectedBrandNames.push(name);
      brandNamesSeen.add(name.toLocaleLowerCase());
    }
  });
  const emailBrandPrefix = selectedBrandNames.join(" + ");
  const selectedMissingPackaging = selectedItems.filter(p => !p.has_packaging_certification);
  const selectedWithBarcode = selectedItems.filter(p => p.has_barcode).length;
  const kindAvailable = {
    sds: selectedWithSds > 0,
    cpnp: selectedWithInci > 0,
    cpnp_notification: selectedWithCpnpNotif > 0,
    pif: selectedWithPif > 0,
    packaging_certification: selectedWithPackagingCertification > 0,
    labels: selectedWithLabels > 0,
    photos: selectedWithPhotos > 0,
    qr: selected.size > 0,
    barcode: selectedWithBarcode > 0,
  };

  const downloadZip = async (kind, extraBody = {}) => {
    if (selected.size === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    const pathMap = {
      sds: "safety-sheets",
      cpnp: "cpnp",
      cpnp_notification: "cpnp-notification",
      pif: "pif",
      packaging_certification: "packaging-certification",
      photos: "photos",
      labels: "labels",
      qr: "qr",
      barcode: "barcode",
    };
    const slug = pathMap[kind];
    if (!slug) return;
    setBusy(kind);
    try {
      const token = getActiveToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/exports/${slug}.zip`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_ids: Array.from(selected),
          ...extraBody,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const fname = m ? m[1] : `${kind}.zip`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`ZIP scaricato: ${fname}`);
    } catch (e) {
      toast.error(e.message || "Errore export");
    } finally {
      setBusy(null);
      setLangModal(false);
      setQrModal(false);
      setBarcodeModal(false);
      setAssetMenuOpen(false);
    }
  };

  const openSds = () => {
    if (selected.size === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    downloadZip("sds", {});
  };
  const openCpnp = () => {
    if (selected.size === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    setLangModal(true);
  };
  const openQr = () => {
    if (selected.size === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    setQrModal(true);
  };
  const openBarcode = () => {
    if (selected.size === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    setBarcodeModal(true);
  };

  const confirmCpnp = () => {
    const langs = Object.entries(cpnpLangs).filter(([, v]) => v).map(([k]) => k).join(",");
    downloadZip("cpnp", { langs });
  };
  const confirmQr = () => {
    downloadZip("qr", { qr_format: qrFormat });
  };
  const confirmBarcode = () => {
    downloadZip("barcode", { barcode_format: barcodeFormat });
  };

  // Route an asset menu pick to its download / sub-modal.
  const pickAsset = (k) => {
    setAssetMenuOpen(false);
    if (k === "cpnp") return openCpnp();
    if (k === "qr") return openQr();
    if (k === "barcode") return openBarcode();
    if (k === "sds") return openSds();
    downloadZip(k);
  };

  const KIND_LABELS = {
    sds: "Schede di Sicurezza",
    cpnp: "Schede CPNP/INCI",
    cpnp_notification: "Notifica CPNP",
    pif: "PIF",
    packaging_certification: "Documento certificazione packaging",
    photos: "Foto prodotti",
    labels: "Etichette",
    qr: "QR Code",
    barcode: "Barcode",
  };

  const ASSET_OPTIONS = [
    { k: "sds", label: "Schede di Sicurezza", Icon: ShieldCheck, count: selectedWithSds, has: (p) => p.has_sds },
    { k: "cpnp", label: "Schede CPNP/INCI", Icon: FileText, count: selectedWithInci, has: (p) => p.inci_approved },
    { k: "cpnp_notification", label: "Notifica CPNP", Icon: ClipboardCheck, count: selectedWithCpnpNotif, has: (p) => p.has_cpnp_notification },
    { k: "pif", label: "PIF", Icon: FolderOpen, count: selectedWithPif, has: (p) => p.has_pif },
    { k: "packaging_certification", label: "Documento certificazione packaging", Icon: PackageCheck, count: selectedWithPackagingCertification, has: (p) => p.has_packaging_certification },
    { k: "labels", label: "Etichette", Icon: Tag, count: selectedWithLabels, has: (p) => p.has_labels },
    { k: "photos", label: "Foto prodotti", Icon: ImageIcon, count: selectedWithPhotos, has: (p) => p.has_photos },
    { k: "qr", label: "QR Code", Icon: QrCode, count: selected.size, always: true, has: () => true },
    { k: "barcode", label: "Barcode", Icon: Barcode, count: selectedWithBarcode, has: (p) => p.has_barcode },
  ];
  const availAssetCount = Object.values(kindAvailable).filter(Boolean).length;
  const HAS_BY_KIND = Object.fromEntries(ASSET_OPTIONS.map(o => [o.k, o.has]));

  const buildDefaultEmail = () => {
    const subject = "Documenti allegati";
    // Keep the body short: the email template already lists the documents
    // (header) and the products included (table), so the message must NOT
    // repeat them — otherwise the same content appears several times.
    const message = `Buongiorno,\n\nin allegato la documentazione richiesta.\n\nCordiali saluti`;
    return { subject, message };
  };

  const selectedKinds = (kindsObj) =>
    Object.entries(kindsObj).filter(([, v]) => v).map(([k]) => k);

  const openEmail = () => {
    if (selected.size === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    setEmailForm(f => {
      const kinds = {};
      Object.keys(f.kinds).forEach(k => { kinds[k] = !!(f.kinds[k] && kindAvailable[k]); });
      if (!selectedKinds(kinds).length) {
        const first = Object.keys(kinds).find(k => kindAvailable[k]);
        if (first) kinds[first] = true;
      }
      const arr = selectedKinds(kinds);
      const { subject, message } = buildDefaultEmail();
      return { ...f, kinds, subject, message };
    });
    setEmailModal(true);
  };

  const toggleEmailKind = (k) => {
    if (!kindAvailable[k]) return;
    setEmailForm(f => {
      const kinds = { ...f.kinds, [k]: !f.kinds[k] };
      const arr = selectedKinds(kinds);
      const { subject, message } = buildDefaultEmail();
      return { ...f, kinds, subject, message };
    });
  };

  const toggleAllEmailKinds = () => {
    setEmailForm(f => {
      const availKeys = Object.keys(f.kinds).filter(k => kindAvailable[k]);
      const allOn = availKeys.length > 0 && availKeys.every(k => f.kinds[k]);
      const next = {};
      Object.keys(f.kinds).forEach(k => { next[k] = allOn ? false : !!kindAvailable[k]; });
      const arr = selectedKinds(next);
      const { subject, message } = buildDefaultEmail();
      return { ...f, kinds: next, subject, message };
    });
  };

  const sendEmail = async () => {
    const recipients = emailForm.recipients
      .split(/[,\s;]+/).map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) { toast.error("Inserisci almeno un destinatario"); return; }
    const kindsArr = selectedKinds(emailForm.kinds);
    if (kindsArr.length === 0) { toast.error("Seleziona almeno un tipo di documento"); return; }
    const langs = Object.entries(emailForm.langs).filter(([, v]) => v).map(([k]) => k).join(",");
    setBusy("email");
    try {
      const token = getActiveToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exports/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          product_ids: Array.from(selected),
          kinds: kindsArr,
          recipients,
          subject: emailForm.subject || undefined,
          message: emailForm.message || undefined,
          langs: kindsArr.includes("cpnp") ? langs : undefined,
          qr_format: kindsArr.includes("qr") ? emailForm.qr_format : undefined,
          barcode_format: kindsArr.includes("barcode") ? emailForm.barcode_format : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      addEmails(recipients);
      const nAtt = data.attachments || kindsArr.length;
      toast.success(`Email inviata a ${data.recipients} destinatari (${nAtt} allegat${nAtt === 1 ? "o" : "i"})`);
      setEmailModal(false);
    } catch (e) {
      toast.error(e.message || "Errore invio email");
    } finally { setBusy(null); }
  };

  const removeFilter = (key) => {
    const next = { ...filters };
    if (key === "price") { next.price_min = ""; next.price_max = ""; }
    else if (key === "category_id") { next.category_id = ""; next.subcategory_id = ""; }
    else next[key] = "";
    setFilters(next);
    fetchAll(next);
  };
  const clearAllFilters = () => { setFilters(EMPTY_FILTERS); fetchAll(EMPTY_FILTERS); };
  const filterChips = [];
  if (filters.q) filterChips.push({ key:"q", label:`Ricerca: "${filters.q}"` });
  if (filters.brand_id) filterChips.push({ key:"brand_id", label:`Brand: ${brandMap[filters.brand_id]?.name || filters.brand_id}` });
  if (filters.category_id) filterChips.push({ key:"category_id", label:`Categoria: ${catMap[filters.category_id]?.name || filters.category_id}` });
  if (filters.subcategory_id) filterChips.push({ key:"subcategory_id", label:`Sotto-categoria: ${catMap[filters.subcategory_id]?.name || filters.subcategory_id}` });
  if (filters.code) filterChips.push({ key:"code", label:`Codice: ${filters.code}` });
  if (filters.barcode) filterChips.push({ key:"barcode", label:`Barcode: ${filters.barcode}` });
  if (filters.inci) filterChips.push({ key:"inci", label:`INCI: ${filters.inci}` });
  if (filters.cas) filterChips.push({ key:"cas", label:`CAS: ${filters.cas}` });
  if (filters.inci_min_percent) filterChips.push({ key:"inci_min_percent", label:`% min: ${filters.inci_min_percent}` });
  if (filters.price_min !== "" || filters.price_max !== "") filterChips.push({ key:"price", label:`Prezzo: ${filters.price_min!==""?filters.price_min:priceBounds.min}–${filters.price_max!==""?filters.price_max:priceBounds.max} €` });

  return (
    <div className="downloads-ux h-full flex flex-col p-4 pt-16 md:p-8 lg:p-12" data-testid="downloads-page">
      <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4 mb-6 md:mb-8">
        <div>
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Esportazioni</div>
          <h1 className="font-serif text-3xl md:text-4xl">Download</h1>
          <div className="text-sm text-[#707070] mt-1">
            <span data-testid="downloads-total-count">{total}</span> prodotti · <span data-testid="downloads-selected-count">{selected.size}</span> selezionati
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="btn-ghost flex items-center gap-2 cursor-pointer" data-testid="only-ready-toggle">
            <input type="checkbox" checked={onlyReady} onChange={(e) => setOnlyReady(e.target.checked)}
                   data-testid="downloads-only-ready-toggle" />
            Solo prodotti pronti
          </label>
          <button onClick={() => setShowFilters(s => !s)}
                  className={`btn-ghost flex items-center ${showFilters ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]" : (filterChips.length ? "!border-[#B45309] !text-[#B45309]" : "")}`}
                  aria-expanded={showFilters} data-testid="toggle-filters-btn">
            <Filter className="w-3 h-3 mr-2" strokeWidth={1.5} />{showFilters ? "Chiudi filtri" : (filterChips.length ? `Filtri (${filterChips.length})` : "Filtri")}
          </button>
        </div>
      </div>

      <div className="downloads-ux-search luxury-card p-4 mb-6 flex items-center gap-3 min-w-0">
        <Search className="w-4 h-4 text-[#707070]" strokeWidth={1.5} />
        <input data-testid="search-input" placeholder="Cerca per nome, codice..."
               className="flex-1 min-w-0 outline-none bg-transparent text-sm"
               value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
               onKeyDown={(e) => e.key === "Enter" && fetchAll()} />
        <button onClick={fetchAll} className="text-xs tracking-luxury uppercase text-[#1A1A1A] hover:text-[#707070]" data-testid="downloads-search-submit">Cerca</button>
      </div>

      {filterChips.length > 0 && (
        <div className="flex items-center flex-wrap gap-2 mb-6 -mt-2" data-testid="active-filters-bar">
          <span className="text-[10px] tracking-luxury uppercase text-[#707070] mr-1">Filtri attivi:</span>
          {filterChips.map(c => (
            <button key={c.key} onClick={() => removeFilter(c.key)}
                    className="inline-flex items-center gap-1.5 text-xs pl-3 pr-2 py-1 bg-[#F4F4F0] border border-[#E5E5E0] rounded-full hover:border-[#C5B39B] transition"
                    data-testid={`active-filter-${c.key}`}>
              {c.label}
              <X className="w-3 h-3 text-[#707070]" strokeWidth={2} />
            </button>
          ))}
          <button onClick={clearAllFilters}
                  className="text-[10px] tracking-luxury uppercase text-[#8C3A3A] hover:opacity-70 ml-1"
                  data-testid="active-filters-clear-all">Pulisci tutto</button>
        </div>
      )}

      {showFilters && (
        <div className="luxury-card p-6 mb-6 relative" data-testid="filters-panel">
          <button onClick={() => setShowFilters(false)}
                  className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-sm text-[#707070] hover:text-[#1A1A1A] hover:bg-[#F4F4F0] transition text-xl leading-none"
                  aria-label="Chiudi filtri" data-testid="downloads-filters-close">×</button>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            <div>
              <label className="label-tiny">Brand</label>
              <select className={`input-luxury ${!filters.brand_id ? "product-ux-select-empty" : ""}`} value={filters.brand_id} data-testid="filter-brand"
                      onChange={(e) => setFilters({ ...filters, brand_id: e.target.value })}>
                <option value="">Tutti</option>
                {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tiny">Categoria</label>
              <select className={`input-luxury ${!filters.category_id ? "product-ux-select-empty" : ""}`} value={filters.category_id} data-testid="filter-category"
                      onChange={(e) => setFilters({ ...filters, category_id: e.target.value, subcategory_id: "" })}>
                <option value="">Tutte</option>
                {topCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tiny">Sotto-categoria</label>
              <select className={`input-luxury ${!filters.subcategory_id ? "product-ux-select-empty" : ""}`} value={filters.subcategory_id} data-testid="filter-subcategory"
                      disabled={!filters.category_id}
                      onChange={(e) => setFilters({ ...filters, subcategory_id: e.target.value })}>
                <option value="">{filters.category_id ? "Tutte" : "Seleziona prima una categoria"}</option>
                {subcats.filter(s => !filters.category_id || s.parent_id === filters.category_id)
                  .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tiny">Codice prodotto</label>
              <input className="input-luxury" data-testid="filter-code" value={filters.code} placeholder="es. PRD-001"
                     onChange={(e) => setFilters({ ...filters, code: e.target.value })} />
            </div>
            <div>
              <label className="label-tiny">Codice a barre</label>
              <input className="input-luxury" data-testid="filter-barcode" value={filters.barcode} placeholder="es. 8001234567890"
                     onChange={(e) => setFilters({ ...filters, barcode: e.target.value })} />
            </div>
            <div>
              <label className="label-tiny">Ingrediente INCI</label>
              <input className="input-luxury" data-testid="filter-inci" value={filters.inci}
                     placeholder="es. HEMA"
                     onChange={(e) => setFilters({ ...filters, inci: e.target.value })} />
            </div>
            <div>
              <label className="label-tiny">Codice CAS</label>
              <input className="input-luxury" data-testid="filter-cas" value={filters.cas} placeholder="es. 7732-18-5"
                     onChange={(e) => setFilters({ ...filters, cas: e.target.value })} />
            </div>
            <div>
              <label className="label-tiny">% minima ingrediente</label>
              <input className="input-luxury" type="number" step="0.1"
                     data-testid="filter-inci-percent" value={filters.inci_min_percent} placeholder="es. 5"
                     onChange={(e) => setFilters({ ...filters, inci_min_percent: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="label-tiny flex items-center justify-between">
                <span>Prezzo (€)</span>
                <span className="text-[#1A1A1A] font-medium tabular-nums">
                  {filters.price_min !== "" ? filters.price_min : priceBounds.min}
                  {" — "}
                  {filters.price_max !== "" ? filters.price_max : priceBounds.max}
                </span>
              </label>
              <div className="pt-3 pb-1">
                <Slider
                  min={priceBounds.min}
                  max={priceBounds.max}
                  step={1}
                  value={[
                    filters.price_min !== "" ? Number(filters.price_min) : priceBounds.min,
                    filters.price_max !== "" ? Number(filters.price_max) : priceBounds.max,
                  ]}
                  onValueChange={([mn, mx]) => setFilters({
                    ...filters,
                    price_min: mn > priceBounds.min ? mn : "",
                    price_max: mx < priceBounds.max ? mx : "",
                  })}
                />
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end gap-3">
              <button onClick={clearAllFilters}
                      className="btn-ghost" data-testid="filters-clear-btn">Pulisci</button>
              <button onClick={() => { fetchAll(); setShowFilters(false); }} className="btn-primary" data-testid="filters-apply-btn">Applica</button>
            </div>
          </div>
        </div>
      )}

      <div className="luxury-card flex-1 min-h-[17rem] overflow-auto pb-48 sm:pb-36 md:pb-24">
        <table className="table-luxury sticky-thead" data-testid="downloads-table">
          <thead>
            <tr>
              <th className="w-10">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll}
                       data-testid="select-all-checkbox"
                       title={allVisibleSelected ? "Deseleziona tutti" : "Seleziona tutti"} />
              </th>
              <SortableTh field="code" label="Codice" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh field="name" label="Nome" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh field="brand" label="Brand" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh field="category" label="Categoria" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center text-[#707070] py-12">Caricamento...</td></tr>
            ) : visibleItems.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-[#707070] py-12">
                {onlyReady ? "Nessun prodotto pronto per l'esportazione con i filtri correnti." : "Nessun prodotto."}
              </td></tr>
            ) : visibleItems.map(p => (
              <tr key={p.id} onClick={() => toggle(p.id)}
                  className={`cursor-pointer ${selected.has(p.id) ? "bg-[#F4F4F0]" : ""}`}
                  data-testid={`download-row-${p.code}`}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}
                         data-testid={`select-${p.code}`} />
                </td>
                <td className="font-mono text-xs">{p.code}</td>
                <td className="font-medium">{p.name}</td>
                <td>{brandMap[p.brand_id]?.name || "—"}</td>
                <td>{catMap[p.category_id]?.name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 md:left-[260px] right-0 bg-white border-t border-[#E5E5E0] shadow-lg z-40"
           data-testid="download-actionbar">
        <div className="px-4 md:px-8 lg:px-12 py-3 md:py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 md:gap-4 text-sm flex-wrap">
            <div className="pl-1 md:pl-2 leading-tight">
              <span className="font-serif text-xl md:text-2xl mr-2 inline-block" data-testid="downloads-actionbar-selected-count">{selected.size}</span>
              <span className="text-[#707070] text-xs md:text-sm">selezionati</span>
            </div>
            {selected.size > 0 && (
              <>
                <ProductStatus tone={availAssetCount === 9 ? "success" : availAssetCount > 0 ? "warning" : "neutral"}
                      testId="asset-availability-summary">
                  {availAssetCount} su 9 asset disponibili
                </ProductStatus>
                <button type="button" onClick={clearSelection} className="btn-ghost !px-3 !py-2 inline-flex items-center gap-2"
                        data-testid="clear-selection-btn">
                  <X className="w-3.5 h-3.5" aria-hidden="true" strokeWidth={1.8} />Pulisci selezione
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <Popover open={assetMenuOpen} onOpenChange={setAssetMenuOpen}>
              <PopoverTrigger asChild>
                <button disabled={selected.size === 0 || busy !== null}
                        className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FAF9F6] px-4 md:px-5 py-2.5 rounded-sm text-sm tracking-wide disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#2A2A2A] transition-colors"
                        data-testid="scarica-primary-dropdown-btn">
                  <Download className="w-4 h-4" strokeWidth={1.5} />
                  Scarica Asset {selected.size > 0 ? `(${selected.size})` : ""}
                  <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" align="end" className="downloads-ux w-[420px] max-w-[calc(100vw-16px)] p-0 border-[#E5E5E0]" data-testid="downloads-asset-menu">
                <div className="px-4 py-3 border-b border-[#F0F0EA]">
                  <div className="text-[10px] tracking-luxury text-[#707070] uppercase">Esporta asset</div>
                  <div className="text-sm text-[#1A1A1A]">{selected.size} prodott{selected.size === 1 ? "o" : "i"} selezionat{selected.size === 1 ? "o" : "i"}</div>
                </div>
                <div className="p-2 max-h-[56vh] overflow-y-auto">
                  {ASSET_OPTIONS.map(opt => {
                    const total = selected.size;
                    const ready = opt.always ? total : opt.count;
                    const disabled = busy !== null || (!opt.always && opt.count === 0);
                    const state = (ready >= total && total > 0) ? "full" : ready > 0 ? "partial" : "zero";
                    const Icon = opt.Icon;
                    const missing = opt.always ? [] : selectedItems.filter(p => !opt.has(p));
                    const isOpen = expandedKind === opt.k;
                    return (
                      <div key={opt.k} className="rounded-sm overflow-hidden">
                        <button onClick={() => !disabled && pickAsset(opt.k)} disabled={disabled}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-left transition ${disabled ? "cursor-not-allowed" : "hover:bg-[#FAFAF7]"}`}
                                data-testid={`export-option-${opt.k}`}
                                title={disabled && !opt.always ? "Nessun prodotto selezionato dispone di questo asset" : ""}>
                          <span className="w-8 h-8 rounded-sm bg-[#F4F4F0] flex items-center justify-center shrink-0">
                            <Icon className="w-4 h-4 text-[#1A1A1A]" strokeWidth={1.5} />
                          </span>
                          <span className="flex-1 min-w-0 text-sm text-[#1A1A1A] truncate">
                            {busy === opt.k ? "Preparazione..." : opt.label}
                          </span>
                          <ProductStatus tone={state === "full" ? "success" : state === "partial" ? "warning" : "neutral"}
                                         className="shrink-0" testId={`export-avail-${opt.k}`}>
                            {opt.always ? `${total} pronti` : `${opt.count}/${total} pronti`}
                          </ProductStatus>
                        </button>
                        {missing.length > 0 && (
                          <div className="pl-11 pr-3 pb-1">
                            <button onClick={(e) => { e.stopPropagation(); setExpandedKind(isOpen ? null : opt.k); }}
                                    className="product-ux-warning-text inline-flex items-center gap-1 text-[11px] hover:opacity-70"
                                    data-testid={`export-missing-toggle-${opt.k}`}>
                              <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                              {missing.length} senza questo asset
                            </button>
                            {isOpen && (
                              <div className="mt-1 max-h-40 overflow-y-auto border border-[#F0EAEA] bg-[#FCF8F8] rounded-sm"
                                   data-testid={`export-missing-list-${opt.k}`}>
                                {missing.map(p => (
                                  <div key={p.id} className="flex items-baseline gap-2 px-2.5 py-1.5 text-[11px] border-b border-[#F5EDED] last:border-0" data-testid={`export-missing-product-${opt.k}-${p.id}`}>
                                    <span className="product-ux-warning-text font-mono shrink-0">{p.code}</span>
                                    <span className="text-[#5A5A5A] truncate">{p.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            <button onClick={openEmail} disabled={busy !== null || selected.size === 0}
                    className="btn-ghost flex items-center gap-2 !border-[#C5B39B] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="send-email-btn"
                    title={selected.size === 0 ? "Seleziona almeno un prodotto" : "Invia via email"}>
              <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Invia via email</span>
            </button>
          </div>
        </div>
      </div>

      {langModal && (
        <div className="downloads-ux fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
             onClick={() => !busy && setLangModal(false)}
             data-testid="cpnp-lang-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl">Lingue CPNP</h3>
              <button onClick={() => setLangModal(false)} disabled={!!busy}
                      className="text-[#707070] hover:text-[#1A1A1A] disabled:opacity-40" data-testid="cpnp-lang-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-[#707070] mb-4">
              Italiano è sempre incluso. Seleziona altre lingue da aggiungere.
            </div>
            <div className="space-y-2 mb-6">
              {[
                { code: "en", label: "Inglese" },
                { code: "fr", label: "Francese" },
                { code: "es", label: "Spagnolo" },
                { code: "de", label: "Tedesco" },
                { code: "pt", label: "Portoghese" },
              ].map(l => (
                <label key={l.code} className="flex items-center gap-3 text-sm cursor-pointer">
                  <input type="checkbox" checked={cpnpLangs[l.code]}
                         onChange={(e) => setCpnpLangs({ ...cpnpLangs, [l.code]: e.target.checked })}
                         data-testid={`cpnp-lang-${l.code}`} />
                  <span>{l.label}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setLangModal(false)} disabled={!!busy}
                      className="btn-ghost" data-testid="cpnp-lang-cancel">Annulla</button>
              <button onClick={confirmCpnp} disabled={!!busy}
                      className="btn-ghost" data-testid="cpnp-lang-confirm">
                {busy ? "Generazione PDF..." : "Scarica ZIP"}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrModal && (
        <div className="downloads-ux fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
             onClick={() => !busy && setQrModal(false)}
             data-testid="qr-format-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl">Formato QR</h3>
              <button onClick={() => setQrModal(false)} disabled={!!busy}
                      className="text-[#707070] hover:text-[#1A1A1A] disabled:opacity-40" data-testid="qr-format-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-[#707070] mb-4">
              Scegli il formato del QR code da esportare.
            </div>
            <div className="space-y-2 mb-6">
              {[
                { code: "png", label: "PNG", note: "Immagine raster, ideale per web e stampa standard" },
                { code: "svg", label: "SVG", note: "Vettoriale, scalabile senza perdita di qualità" },
                { code: "pdf", label: "PDF", note: "Documento pronto per la stampa" },
              ].map(l => (
                <label key={l.code} className="flex items-start gap-3 text-sm cursor-pointer p-2 border border-transparent hover:border-[#E5E5E0] rounded-sm">
                  <input type="radio" name="qr-format" checked={qrFormat === l.code}
                         onChange={() => setQrFormat(l.code)}
                         className="mt-1"
                         data-testid={`qr-format-${l.code}`} />
                  <span>
                    <span className="font-medium">{l.label}</span>
                    <span className="block text-xs text-[#707070] mt-0.5">{l.note}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setQrModal(false)} disabled={!!busy}
                      className="btn-ghost" data-testid="qr-format-cancel">Annulla</button>
              <button onClick={confirmQr} disabled={!!busy}
                      className="btn-ghost" data-testid="qr-format-confirm">
                {busy ? "Preparazione..." : "Scarica ZIP"}
              </button>
            </div>
          </div>
        </div>
      )}

      {barcodeModal && (
        <div className="downloads-ux fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
             onClick={() => !busy && setBarcodeModal(false)}
             data-testid="barcode-format-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl">Formato Barcode</h3>
              <button onClick={() => setBarcodeModal(false)} disabled={!!busy}
                      className="text-[#707070] hover:text-[#1A1A1A] disabled:opacity-40" data-testid="barcode-format-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-[#707070] mb-4">
              Scegli il formato del codice a barre. Se un prodotto non ha un file caricato, viene generato da numero (EAN-13/EAN-8, altrimenti Code128).
            </div>
            <div className="space-y-2 mb-6">
              {[
                { code: "png", label: "PNG", note: "Immagine raster, ideale per web e stampa standard" },
                { code: "svg", label: "SVG", note: "Vettoriale, scalabile senza perdita di qualità" },
                { code: "pdf", label: "PDF", note: "Documento stampabile, barcode centrato su pagina" },
              ].map(l => (
                <label key={l.code} className="flex items-start gap-3 text-sm cursor-pointer p-2 border border-transparent hover:border-[#E5E5E0] rounded-sm">
                  <input type="radio" name="barcode-format" checked={barcodeFormat === l.code}
                         onChange={() => setBarcodeFormat(l.code)} className="mt-1"
                         data-testid={`barcode-format-${l.code}`} />
                  <span>
                    <span className="font-medium">{l.label}</span>
                    <span className="block text-xs text-[#707070] mt-0.5">{l.note}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setBarcodeModal(false)} disabled={!!busy}
                      className="btn-ghost" data-testid="barcode-format-cancel">Annulla</button>
              <button onClick={confirmBarcode} disabled={!!busy}
                      className="btn-ghost" data-testid="barcode-format-confirm">
                {busy ? "Preparazione..." : "Scarica ZIP"}
              </button>
            </div>
          </div>
        </div>
      )}

      {emailModal && (
        <div className="downloads-ux fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
             onClick={() => !busy && setEmailModal(false)}
             data-testid="email-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-serif text-2xl">Invia via email</h3>
                <div className="text-xs text-[#707070] mt-1">
                  {selected.size} prodotti selezionati
                </div>
              </div>
              <button onClick={() => setEmailModal(false)} disabled={!!busy}
                      className="text-[#707070] hover:text-[#1A1A1A] disabled:opacity-40" data-testid="email-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <label className="label-tiny">Documenti da allegare</label>
                  <button type="button" onClick={toggleAllEmailKinds}
                          className="text-[11px] text-[#8A7A5C] hover:text-[#1A1A1A] underline underline-offset-2"
                          data-testid="email-kinds-all">
                    {Object.keys(emailForm.kinds).filter(k => kindAvailable[k]).every(k => emailForm.kinds[k])
                      ? "Deseleziona tutti" : "Tutti gli allegati"}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {[
                    { k: "sds", l: "Schede di Sicurezza" },
                    { k: "cpnp", l: "Schede CPNP/INCI" },
                    { k: "cpnp_notification", l: "Notifica CPNP" },
                    { k: "pif", l: "PIF" },
                    { k: "packaging_certification", l: "Documento certificazione packaging" },
                    { k: "labels", l: "Etichette" },
                    { k: "photos", l: "Foto" },
                    { k: "qr", l: "QR Code" },
                    { k: "barcode", l: "Barcode" },
                  ].map(x => {
                    const avail = kindAvailable[x.k];
                    const option = ASSET_OPTIONS.find(opt => opt.k === x.k);
                    const ready = option?.always ? selected.size : (option?.count || 0);
                    return (
                    <label key={x.k}
                           title={avail ? "" : "Nessun prodotto selezionato ha questo documento"}
                           className={`flex items-center flex-wrap gap-2 text-sm border rounded-sm px-3 py-2 transition-colors ${
                             !avail ? "border-[#EDEDE7] bg-[#F7F7F3] cursor-not-allowed"
                                    : emailForm.kinds[x.k] ? "border-[#1A1A1A] bg-[#FAFAF7] cursor-pointer"
                                                            : "border-[#E5E5E0] cursor-pointer"}`}>
                      <input type="checkbox" checked={!!emailForm.kinds[x.k]} disabled={!avail}
                             onChange={() => toggleEmailKind(x.k)}
                             data-testid={`email-kind-${x.k}`} />
                      <span className="min-w-0 break-words flex-1">{x.l}</span>
                      <ProductStatus tone={ready === 0 ? "warning" : ready === selected.size ? "success" : "warning"}
                                     testId={`email-kind-availability-${x.k}`}>
                        {ready === 0 ? "Mancante" : ready === selected.size ? "Pronto" : `${ready}/${selected.size} pronti`}
                      </ProductStatus>
                    </label>
                    );
                  })}
                </div>
                <div className="text-[10px] text-[#707070] mt-1">
                  Puoi inviare più tipi insieme: verrà allegato un archivio ZIP per ciascuno. Se il peso totale supera 25 MB l'invio verrà bloccato con un avviso.
                </div>
              </div>

              <div className={`border rounded-sm p-3 ${selectedMissingPackaging.length ? "workflow-ux-panel--warning" : "workflow-ux-panel--success"}`}
                   data-testid="email-packaging-availability">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs font-medium" data-testid="email-packaging-availability-count">
                    Certificazione Packaging: {selectedWithPackagingCertification}/{selectedItems.length} prodotti disponibili
                  </div>
                  <ProductStatus tone={selectedMissingPackaging.length ? "warning" : "success"}
                                 testId="email-packaging-availability-status">
                    {selectedMissingPackaging.length ? "Mancante" : "Disponibile"}
                  </ProductStatus>
                </div>
                {selectedMissingPackaging.length > 0 && (
                  <div className="mt-2" data-testid="email-missing-list-packaging_certification">
                    <div className="text-xs mb-1">
                      {selectedMissingPackaging.length} senza certificazione packaging
                    </div>
                    <div className="max-h-32 overflow-y-auto divide-y divide-[#F0EAEA] border border-[#F0EAEA] bg-white">
                      {selectedMissingPackaging.map(p => (
                        <div key={p.id} className="flex flex-wrap gap-x-2 px-2.5 py-1.5 text-xs"
                             data-testid={`email-packaging-missing-product-${p.id}`}>
                          <span className="product-ux-warning-text font-mono" data-testid={`email-packaging-missing-code-${p.id}`}>{p.code}</span>
                          <span className="text-[#5A5A5A] break-words" data-testid={`email-packaging-missing-name-${p.id}`}>{p.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {(() => {
                const sel = Object.keys(emailForm.kinds).filter(k => emailForm.kinds[k] && k !== "qr" && k !== "packaging_certification");
                const rows = sel.map(k => {
                  const has = HAS_BY_KIND[k] || (() => true);
                  return { k, missing: selectedItems.filter(p => !has(p)) };
                }).filter(r => r.missing.length > 0);
                if (rows.length === 0) return null;
                return (
                  <div className="workflow-ux-panel--warning border rounded-sm p-3" data-testid="email-missing-panel">
                    <div className="text-[11px] mb-2">
                      Attenzione: alcuni prodotti selezionati non hanno l'elemento da inviare (verranno esclusi dall'allegato).
                    </div>
                    <div className="space-y-1.5">
                      {rows.map(r => {
                        const open = emailExpandedKind === r.k;
                        return (
                          <div key={r.k}>
                            <button type="button" onClick={() => setEmailExpandedKind(open ? null : r.k)}
                                    className="product-ux-warning-text inline-flex items-center gap-1 text-[11px] hover:opacity-70"
                                    data-testid={`email-missing-toggle-${r.k}`}>
                              <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
                              {KIND_LABELS[r.k]}: {r.missing.length} senza questo elemento
                            </button>
                            {open && (
                              <div className="mt-1 max-h-32 overflow-y-auto border border-[#F0EAEA] bg-white rounded-sm"
                                   data-testid={`email-missing-list-${r.k}`}>
                                {r.missing.map(p => (
                                  <div key={p.id} className="flex items-baseline gap-2 px-2.5 py-1 text-[11px] border-b border-[#F5EDED] last:border-0">
                                    <span className="product-ux-warning-text font-mono shrink-0" data-testid={`email-missing-code-${r.k}-${p.id}`}>{p.code}</span>
                                    <span className="text-[#5A5A5A] truncate">{p.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {emailForm.kinds.cpnp && (
                <div>
                  <label className="label-tiny">Lingue CPNP (IT sempre inclusa)</label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {[{ c: "en", l: "EN" }, { c: "fr", l: "FR" }, { c: "es", l: "ES" }, { c: "de", l: "DE" }, { c: "pt", l: "PT" }].map(x => (
                      <label key={x.c} className="flex items-center gap-1.5 text-sm">
                        <input type="checkbox" checked={emailForm.langs[x.c]}
                               onChange={(e) => setEmailForm({
                                 ...emailForm,
                                 langs: { ...emailForm.langs, [x.c]: e.target.checked },
                               })}
                               data-testid={`email-lang-${x.c}`} />
                        {x.l}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {emailForm.kinds.qr && (
                <div>
                  <label className="label-tiny">Formato QR</label>
                  <div className="flex gap-4 mt-2">
                    {["png", "svg", "pdf"].map(f => (
                      <label key={f} className="flex items-center gap-1.5 text-sm">
                        <input type="radio" name="email-qr-format" checked={emailForm.qr_format === f}
                               onChange={() => setEmailForm({ ...emailForm, qr_format: f })}
                               data-testid={`email-qr-format-${f}`} />
                        {f.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {emailForm.kinds.barcode && (
                <div>
                  <label className="label-tiny">Formato Barcode</label>
                  <div className="flex gap-4 mt-2">
                    {["png", "svg", "pdf"].map(f => (
                      <label key={f} className="flex items-center gap-1.5 text-sm">
                        <input type="radio" name="email-barcode-format" checked={emailForm.barcode_format === f}
                               onChange={() => setEmailForm({ ...emailForm, barcode_format: f })}
                               data-testid={`email-barcode-format-${f}`} />
                        {f.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="label-tiny">Destinatari *</label>
                <RecipientsField
                  value={emailForm.recipients}
                  onChange={(v) => setEmailForm({ ...emailForm, recipients: v })}
                  placeholder="uno@esempio.it, due@esempio.it"
                  testIdPrefix="email"
                />
                <div className="text-[10px] text-[#707070] mt-1">
                  Separa più indirizzi con virgola, spazio o punto e virgola.
                </div>
              </div>

              <div>
                <label className="label-tiny">Oggetto</label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <span className="text-sm font-medium break-words text-[#1A1A1A]"
                        data-testid="email-subject-brand-prefix">{emailBrandPrefix} /</span>
                  <input className="input-luxury w-full min-w-0 sm:flex-1" placeholder="Documenti allegati"
                         value={emailForm.subject}
                         onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                         data-testid="email-subject-input" />
                </div>
              </div>

              <div>
                <label className="label-tiny">Messaggio (opzionale)</label>
                <textarea className="input-luxury min-h-[100px]" rows={4}
                          placeholder="Gentile cliente, in allegato trovi..."
                          value={emailForm.message}
                          onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                          data-testid="email-message-input" />
                <div className="text-[10px] text-[#707070] mt-1">
                  Il messaggio appare prima del riepilogo. Il footer con il brand del primo prodotto è aggiunto automaticamente.
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEmailModal(false)} disabled={!!busy}
                      className="btn-ghost" data-testid="email-cancel">Annulla</button>
              <button onClick={sendEmail} disabled={!!busy}
                      className="btn-ghost" data-testid="email-send">
                {busy === "email" ? "Invio..." : "Invia"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SortableTh({ field, label, sortField, sortDir, onSort }) {
  const active = sortField === field;
  const Icon = !active ? ChevronsUpDown : (sortDir === "asc" ? ArrowUp : ArrowDown);
  return (
    <th onClick={() => onSort(field)}
        className={`cursor-pointer select-none group ${active ? "text-[#1A1A1A]" : ""}`}
        data-testid={`th-sort-${field}`}
        title={`Ordina per ${label}${active ? (sortDir === "asc" ? " (crescente)" : " (decrescente)") : ""}`}>
      <span className="inline-flex items-center gap-1.5">
        <span>{label}</span>
        <Icon className={`w-3 h-3 transition ${active ? "text-[#1A1A1A]" : "text-[#C5B39B] opacity-70 group-hover:opacity-100"}`} strokeWidth={2}/>
      </span>
    </th>
  );
}
